/**
 * This file is needed to avoid a circular dependency between two modules:
 * InvitePeopleContext and analytics (which causes a runtime error in Webpack).
 *
 * `useAnalytics()` grabs common attributes from `InvitePeopleContext`,
 * but `InvitePeopleContext` needs to fire analytics after fetching some data.
 *
 * This file can be imported by both modules, with analytics conditionally using
 * the raw InvitePeopleStateContext.
 */
import React, { useContext } from 'react';
import type { StandardCohorts } from '@atlassian/experimental-features/types';

import type { VIRALITY_GROWTH_INITIATIVE } from '../../experiments/constants';
import type { Plugins } from '../../plugins/types';
import type {
	AnalyticsAttributes,
	ApplicationEdition,
	FeaturedUserProvider,
	InviteeProvider,
	SubProduct,
	UserRole,
	ViewAs,
} from '../../types';

import type { FLOW_CORE_INVITES } from './InvitePeopleContext';

export type InvitePeopleState = {
	flow: typeof FLOW_CORE_INVITES;
	growthInitiative: typeof VIRALITY_GROWTH_INITIATIVE;
	inviteeProvider?: InviteeProvider;
	cloudId: string | undefined;
	isLoading: boolean;
	inviteFromAssignModalCohort: StandardCohorts;
	invitePromptOnLinkV3Cohort?: boolean;
	// TODO: start using `JiraSubProduct` here instead of `string` once `SubProduct` is updated along with `normalizeJiraSubProduct`'s type
	jiraSubProduct: string | undefined;
	loggedInEmailDomain: string | undefined;
	plugins?: Plugins;
	productId: string;
	productTitle: string;
	userRole: UserRole;
	userEmail: string | undefined;
	accountId?: string;
	isEligibleForInviteAndAssignFromBoard?: boolean;
	source: string;
	subProduct?: SubProduct;
	viewAs: ViewAs;
	disableNoInviteError: boolean;
	orgId?: string;
	applicationEdition?: ApplicationEdition;
	experimental_featuredUsers?: FeaturedUserProvider;
	experimental_inlineThirdPartyButtons?: boolean;
	experimental_productContext?: Record<string, string>;
	isSimplified?: boolean;
	decorateAnalyticsAttributes?: (attributes: AnalyticsAttributes) => AnalyticsAttributes;
};

export const InvitePeopleStateContext = React.createContext<InvitePeopleState | undefined>(
	undefined,
);

export const useInvitePeopleContext = () => {
	const state = useContext(InvitePeopleStateContext);

	if (state === undefined) {
		throw new Error(
			'useInvitePeopleContext must be used within a InvitePeopleStateContextProvider',
		);
	}

	return state;
};
